import { Skeleton } from "@/components/ui/skeleton";
import PercentageText from "@/screens/creatives/components/table/PercentageText";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RiBarChartFill } from "@remixicon/react";
import { IconAlertTriangle, IconDatabase } from "@tabler/icons-react";

interface CardMetricsProps {
  metrics: Array<{
    label: string;
    value: string;
    key: string;
    warning?: string;
    external?: boolean;
    percentChange: number | string | null | undefined;
    isPercentLoading?: boolean;
  }>;
  isAdView?: boolean;
}

const CardMetrics = ({ metrics, isAdView = false }: CardMetricsProps) => {
  return (
    <div className="w-full flex flex-col gap-[8px] self-stretch pt-[8px]">
      <div className="flex w-[163px] items-center gap-[6px] px-[4px]">
        <RiBarChartFill size={14} className="text-brand-primary-400" />
        <h4 className="overflow-hidden text-ellipsis text-[12px] font-normal leading-[19.16px] tracking-[0.24px] text-brand-primary-800 relative top-[1px]">
          {isAdView ? "Metrics" : "Group Metrics"}
        </h4>
      </div>

      <div className="w-full flex flex-col justify-center items-start gap-[6px] p-2 rounded-[10px] squircle squircle-radius-[12px] squircle-outline-[1px] squircle-outline-color-border-gray">
        {metrics.map(
          ({ label, value, key, warning, external, percentChange, isPercentLoading }) => (
            <div key={key} className="w-full flex justify-between items-center gap-2">
              <div className="flex flex-1 min-w-0">
                <div className="flex items-center gap-1 min-w-0">
                  {external && (
                    <IconDatabase
                      size={16}
                      color="rgb(104 116 156)"
                      className="shrink-0 relative -top-[1px]"
                    />
                  )}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="overflow-hidden text-brand-primary-400 text-ellipsis whitespace-nowrap text-[12px] font-normal leading-[19.157px] tracking-[0.24px] min-w-0">
                          {label}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        align="start"
                        collisionPadding={10}
                        className="max-w-[224px] h-auto text-wrap text-left whitespace-pre-line"
                      >
                        {label}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {warning && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <IconAlertTriangle
                            size={16}
                            color="#F18400"
                            className="shrink-0"
                          />
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          align="start"
                          collisionPadding={10}
                          className="max-w-[224px] h-auto text-wrap text-left whitespace-pre-line"
                        >
                          {warning}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>
              <div className="flex flex-1 min-w-0 justify-end gap-1 items-center">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="overflow-hidden text-brand-primary-800 text-ellipsis whitespace-nowrap text-[12px] font-normal leading-[19.157px] tracking-[0.24px] min-w-0 text-right">
                        <>
                          {value === "undefined" ? (
                            <Skeleton className="w-[40px] h-[12px]" />
                          ) : (
                            <>{value === "N/A" ? "—" : value}</>
                          )}
                        </>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      align="start"
                      collisionPadding={10}
                      className="max-w-[224px] h-auto text-wrap text-left whitespace-pre-line"
                    >
                      <>{value === "undefined" ? "Loading..." : <>{value}</>}</>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {(() => {
                  const pc = percentChange;
                  const isLoading =
                    Boolean(isPercentLoading) ||
                    (typeof pc === "string" && pc.toUpperCase() === "UNDEFINED");
                  const hasValue = pc !== null && pc !== undefined && pc !== "";
                  const isPositive =
                    typeof pc === "number"
                      ? pc > 0
                      : typeof pc === "string"
                        ? pc.trim().startsWith("+")
                        : false;
                  return isLoading || hasValue ? (
                    <PercentageText
                      value={pc}
                      isLoading={isLoading}
                      className={`font-normal not-italic text-[11px] leading-[20px] tracking-[0] ${
                        isPositive ? "text-[#039F54]" : "text-[#02185999]"
                      }`}
                      skeletonWidthClass="w-[36px]"
                    />
                  ) : null;
                })()}
              </div>
            </div>
          ),
        )}
      </div>
    </div>
  );
};

export default CardMetrics;
